import type { CSSProperties } from 'react';
import { Slider, IconButton, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { t } from '../utils/strings';

const SLIDER_HEIGHT = '40vh';

// Match the year-color gradient from pointLayer.jsx
const YEAR_COLOR_FROM_RGB = [0x1A, 0xB8, 0xDA]; // light teal/cyan — earliest year
const YEAR_COLOR_TO_RGB   = [0x0D, 0x2C, 0x40]; // very dark navy — latest year
const YEAR_COLOR_FROM = '#1AB8DA';
const YEAR_COLOR_TO   = '#0D2C40';

function lerp(a: number, b: number, t: number) { return Math.round(a + (b - a) * t); }

function colorForYear(year: number, min: number, max: number): string {
  if (max <= min) return YEAR_COLOR_FROM;
  const t = Math.max(0, Math.min(1, (year - min) / (max - min)));
  const r = lerp(YEAR_COLOR_FROM_RGB[0], YEAR_COLOR_TO_RGB[0], t);
  const g = lerp(YEAR_COLOR_FROM_RGB[1], YEAR_COLOR_TO_RGB[1], t);
  const b = lerp(YEAR_COLOR_FROM_RGB[2], YEAR_COLOR_TO_RGB[2], t);
  return `rgb(${r},${g},${b})`;
}

const YearRangeSlider = ({ yearBounds, yearRange, setYearRange, lang, orientation = 'vertical' }: {
  yearBounds: [number, number] | null,
  yearRange: [number, number] | null,
  setYearRange: (v: [number, number] | null) => void,
  lang: string,
  orientation?: 'vertical' | 'horizontal',
}) => {
  if (!yearBounds) return null;
  const [min, max] = yearBounds;
  if (max <= min) return null;
  const value: [number, number] = yearRange ?? [min, max];
  const isFiltered = yearRange !== null && (yearRange[0] !== min || yearRange[1] !== max);

  // Vertical: lower handle (data-index="0") = early/bottom; upper (data-index="1") = late/top.
  // Horizontal: lower handle = early/left; upper = late/right.
  const lowerColor = colorForYear(value[0], min, max);
  const upperColor = colorForYear(value[1], min, max);
  const isHorizontal = orientation === 'horizontal';

  // Container positioning differs between layouts:
  //   vertical   — top-left of map, fixed slider-height column
  //   horizontal — full-width bar pinned to bottom of map (above the optional thumb panel space)
  const containerStyle: CSSProperties = isHorizontal
    ? {
        position: 'absolute',
        bottom: 8,
        left: 40,
        right: 40,
        zIndex: 1,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        backgroundColor: 'rgba(255,255,255,0.85)',
        borderRadius: 6,
        padding: '6px 4px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
        backdropFilter: 'blur(4px)',
      }
    : {
        position: 'absolute',
        top: 8,
        left: 8,
        height: SLIDER_HEIGHT,
        zIndex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundColor: 'rgba(255,255,255,0.85)',
        borderRadius: 6,
        padding: '8px 4px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
        backdropFilter: 'blur(4px)',
      };

  // Slider gradient and thumb sizing — direction differs per orientation.
  const sliderSx: any = isHorizontal
    ? {
        flex: 1,
        '& .MuiSlider-rail': { opacity: 1, backgroundColor: 'rgba(0,0,0,0.2)', height: 2 },
        '& .MuiSlider-track': {
          opacity: 1, border: 'none', backgroundColor: 'transparent',
          backgroundImage: `linear-gradient(to right, ${YEAR_COLOR_FROM}, ${YEAR_COLOR_TO})`,
          height: 5,
        },
        '& .MuiSlider-thumb[data-index="0"]': { backgroundColor: lowerColor, borderColor: lowerColor, width: 16, height: 16 },
        '& .MuiSlider-thumb[data-index="1"]': { backgroundColor: upperColor, borderColor: upperColor, width: 16, height: 16 },
        color: YEAR_COLOR_TO,
      }
    : {
        height: '100%',
        '& .MuiSlider-rail': { opacity: 1, backgroundColor: 'rgba(0,0,0,0.2)', width: 2 },
        '& .MuiSlider-track': {
          opacity: 1, border: 'none', backgroundColor: 'transparent',
          backgroundImage: `linear-gradient(to top, ${YEAR_COLOR_FROM}, ${YEAR_COLOR_TO})`,
          width: 5,
        },
        '& .MuiSlider-thumb[data-index="0"]': { backgroundColor: lowerColor, borderColor: lowerColor, width: 14, height: 14 },
        '& .MuiSlider-thumb[data-index="1"]': { backgroundColor: upperColor, borderColor: upperColor, width: 14, height: 14 },
        color: YEAR_COLOR_TO,
      };

  if (isHorizontal) {
    return (
      <div style={{ ...containerStyle, gap: 12 }}>
        {/* Left spacer mirrors the right-side reset-button slot so the slider
            track is centered within the bar regardless of filter state. */}
        <div style={{ width: 18, flexShrink: 0 }} />
        <Tooltip title={t('yearRangeFilter', lang)} placement="top">
          <Typography variant="caption" sx={{ color: YEAR_COLOR_TO, fontWeight: 600, fontSize: 11, flexShrink: 0 }}>
            {value[0]}
          </Typography>
        </Tooltip>
        <Slider
          orientation="horizontal"
          min={min}
          max={max}
          value={value}
          onChange={(_, v) => setYearRange(v as [number, number])}
          valueLabelDisplay="off"
          size="small"
          sx={sliderSx}
        />
        <Typography variant="caption" sx={{ color: YEAR_COLOR_TO, fontWeight: 600, fontSize: 11, flexShrink: 0 }}>
          {value[1]}
        </Typography>
        <div style={{ width: 18, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {isFiltered && (
            <Tooltip title={t('clearYearRange', lang)} placement="top">
              <IconButton size="small" onClick={() => setYearRange(null)} sx={{ p: '1px' }}>
                <CloseIcon sx={{ fontSize: 14, color: YEAR_COLOR_TO }} />
              </IconButton>
            </Tooltip>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <Tooltip title={t('yearRangeFilter', lang)} placement="right">
        <Typography variant="caption" sx={{ color: YEAR_COLOR_TO, fontWeight: 600, fontSize: 11, mb: 1.5 }}>
          {value[1]}
        </Typography>
      </Tooltip>
      <Slider
        orientation="vertical"
        min={min}
        max={max}
        value={value}
        onChange={(_, v) => setYearRange(v as [number, number])}
        valueLabelDisplay="off"
        size="small"
        sx={sliderSx}
      />
      <Typography variant="caption" sx={{ color: YEAR_COLOR_TO, fontWeight: 600, fontSize: 11, mt: 1.5 }}>
        {value[0]}
      </Typography>
      <div style={{ height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {isFiltered && (
          <Tooltip title={t('clearYearRange', lang)} placement="right">
            <IconButton size="small" onClick={() => setYearRange(null)} sx={{ p: '2px' }}>
              <CloseIcon sx={{ fontSize: 14, color: YEAR_COLOR_TO }} />
            </IconButton>
          </Tooltip>
        )}
      </div>
    </div>
  );
};

export default YearRangeSlider;
